import React from 'react';
import moment from 'moment';
import 'moment/locale/fr-ca';
import 'moment/locale/fr';
import { siteCulture } from 'settings/vistaprintContext';
import { colors, fontFamily, lineHeight } from 'settings/theme';
import ScreenSizeUtil from 'utils/ScreenSizeUtil';
import Loader from 'components/vistaprintUILibrary/Loader';
import Button from 'components/vistaprintUILibrary/Button';
import ErrorMessage from 'components/vistaprintUILibrary/ErrorMessage';
import Radium from 'radium';
import FontAwesome from 'react-fontawesome';
import { getLocalizedText } from '@vp-dst/localization-util';

const RadiumFontAwesome = Radium(FontAwesome);

export default class DateSelect extends React.Component {
    constructor(props) {
        super(props);

        this.setMomentLocale.bind(this);

        let upcomingDates = [];
        for (var i = 0; i < props.totalDatesToShow; i++) {
            upcomingDates.push(moment().add(i, "days").format("YYYY-MM-DD"));
        }

        this.state = {
            upcomingDates,
            datesToShow: upcomingDates.slice(0, props.numDatesToShowPerPage),
            thisWeek: true,
            nextWeek: false,
            showNextButton: false,
            showPrevButton: false,
            currentPage: 0,
            currentStartIndex: 0,
        };

        this.showNext = this.showNext.bind(this);
        this.showPrev = this.showPrev.bind(this);
    }

    componentDidUpdate(previousProps) {
        if (JSON.stringify(previousProps.availableDates) !== JSON.stringify(this.props.availableDates)) {
            if (this.props.availableDates.length > 0) {
                const numAvailableDays = this.state.datesToShow.filter(date => this.props.availableDates.includes(date)).length;
                this.setState({
                    showNextButton: numAvailableDays <= 5 && numAvailableDays > 0 ? true : false,
                    // if there are no available days this week, show next week only
                    datesToShow: numAvailableDays > 0 ? this.state.datesToShow : this.state.upcomingDates.slice(this.props.numDatesToShowPerPage, 2 * this.props.numDatesToShowPerPage)
                })
            }
        }
    }    
    
    setMomentLocale(locale){
        if(!locale)
        {
            locale = siteCulture;
        }
        if(locale.indexOf("en") !== -1){
            moment.locale("en");
        }
        else if(locale.indexOf("fr-ca") !== -1)
        {
            moment.locale("fr-ca");
        }
        else if(locale.indexOf("fr") !== -1)
        {
            moment.locale("fr");
        }
    }

    showNext() {                         
        const nextPageStartIndex = (this.state.currentPage + 1) * this.props.numDatesToShowPerPage;
        const datesToShow = this.state.upcomingDates.slice(nextPageStartIndex, nextPageStartIndex + this.props.numDatesToShowPerPage);
        const nextWeekAvailableDays = this.props.availableDates.filter(date => datesToShow.includes(date));
        const firstAvailableDay = nextWeekAvailableDays[0];

        this.reloadTimesAvailable(firstAvailableDay);
        this.setState({
            nextWeek: true,
            thisWeek: false,
            showPrevButton: true,
            showNextButton: nextPageStartIndex + this.props.numDatesToShowPerPage >= this.state.upcomingDates.length ? false : true,
            datesToShow,
            currentPage: this.state.currentPage + 1
        })
    }

    showPrev() {
        const prevPageStartIndex = (this.state.currentPage - 1) * this.props.numDatesToShowPerPage;
        const datesToShow = this.state.upcomingDates.slice(prevPageStartIndex, prevPageStartIndex + this.props.numDatesToShowPerPage);
        const thisWeekAvailableDays = this.props.availableDates.filter(date => datesToShow.includes(date));
        const firstAvailableDay = thisWeekAvailableDays[0];

        this.reloadTimesAvailable(firstAvailableDay);
        this.setState({
            nextWeek: false,
            thisWeek: true,
            showPrevButton: prevPageStartIndex === 0 ? false : true,
            showNextButton: true,
            datesToShow,
            currentPage: this.state.currentPage - 1,
        })
    }


    reloadTimesAvailable(firstAvailableDay) {
        const momentDate = moment(firstAvailableDay);
        const day = momentDate.format('ddd').toLowerCase();

        this.props.selectedDateOnChange(firstAvailableDay, day)
    }

    render() {
        const {
            loading,
            errorMessage,
            refreshAvailableDates,
            availableDates,
            selectedDate,
            selectedDateOnChange,
            ...restProps,
        } = this.props;

        if (loading) {
            return (<Loader text={getLocalizedText('loading-available-dates')} />);
        }

        if (errorMessage || availableDates.length === 0) {
            return (
                <div
                    style={{
                        paddingTop: 20,
                        paddingBottom: 20,

                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <ErrorMessage containerStyle={{ textAlign: 'center' }} text={errorMessage} />
                    <Button
                        text="Refresh dates"
                        type="secondary"
                        onClick={refreshAvailableDates}
                    />
                </div>
            );
        }

        if (availableDates.length === 0) {
            return null;
        }

        const screenClass = ScreenSizeUtil.getScreenClass();
        const smallScreen = ScreenSizeUtil.isSmallScreen(screenClass);

        const numberFontSize = {
            'xs': '2em',
            'sm': '2em',
            'md': '2.5em',
            'lg': '3em',
            'xl': '4em',
        };

        const dayFontSize = {
            'xs': '1em',
            'sm': '1em',
            'md': '1.5em',
            'lg': '2em',
            'xl': '2em',
        };

        return (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: smallScreen ? 'flex-start' : 'space-evenly',
                    alignItems: 'center',
                    padding: 2,
                    overflow: smallScreen ? 'auto' : 'hidden',
                }}
                {...restProps}
            >
                {this.state.showPrevButton &&
                <span onClick={this.showPrev}>
                        <RadiumFontAwesome
                            style={{
                                color: colors.carbon,
                                cursor: 'pointer',
                                ':hover': {
                                    color: colors.blueMid,
                                },
                            }}
                            name="chevron-left"
                            size="4x"
                        />
                    </span>
                }

                {this.state.datesToShow.map((dateInfo, index) => {
                    const date = moment(dateInfo);
                    const available = availableDates.some(availableDate => availableDate === dateInfo);

                    this.setMomentLocale("en-us");
                    const dayOfTheWeek = moment(dateInfo).format('ddd').toLowerCase(); // tracking
                    this.setMomentLocale();
          
                    let selected = false;
                    if (selectedDate) {
                        selected = dateInfo === selectedDate;
                    }

                    let backgroundColor;
                    if (selected) {
                        backgroundColor = colors.blueVeryLight;
                    } else {
                        backgroundColor = 'white';
                    }

                    let borderColor;
                    if (!available) {
                        borderColor = colors.disabled;
                    } else if (selected) {
                        borderColor = colors.blueMid;
                    } else {
                        borderColor = colors.border;
                    }

                    return (
                        <div
                            key={`dates-${index}`}
                            onClick={available && !selected ? () => selectedDateOnChange(dateInfo, dayOfTheWeek) : null}
                            style={{
                                boxSizing: 'border-box',
                                height: 110,
                                width: 110,
                                minWidth: 55,
                                marginLeft: 10,
                                marginRight: 10,
                                border: selected ? `3px solid ${borderColor}` : `1px solid ${borderColor}`,
                                borderRadius: 3,

                                backgroundColor,

                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',

                                cursor: 'pointer',
                            }}
                        >
                            <div
                                style={{
                                    fontFamily,
                                    fontSize: numberFontSize[screenClass],
                                    lineHeight,
                                    color: available ? '' : colors.disabled,
                                }}
                            >
                                {date.format("D")}
                            </div>
                            <div
                                style={{
                                    fontFamily,
                                    fontSize: dayFontSize[screenClass],
                                    lineHeight,
                                    color: available ? '' : colors.disabled,
                                }}
                            >
                                {date.format("ddd")}
                            </div>
                        </div>
                    );
                })}
                {this.state.showNextButton &&
                    <span onClick={this.showNext}>
                        <RadiumFontAwesome
                            style={{
                                color: colors.carbon,
                                cursor: 'pointer',
                                ':hover': {
                                    color: colors.blueMid,
                                },
                            }}
                            name="chevron-right"
                            size="4x"
                        />
                    </span>
                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Scheduler/DateSelect.js